import { Button } from "primereact/button";

/**
 * Google Meet Style Control Buttons
 * Icon-only circular buttons with tooltips for professional banking UI
 *
 * Flow: Connect -> Join Meeting -> Start Meeting -> (Media Controls + End)
 */
export const ControlButtons = ({
  isAdminOrCM,
  connected,
  hasJoined,
  isAnalyzing,
  hasAnalysisStarted,
  isVideoOn,
  isAudioOn,
  handleStartCall,
  handleJoinMeeting,
  handleAnalyzeToggle,
  handleCloseMeeting,
  toggleVideo,
  toggleAudio,
  callStarted,
  handleStartTheCall,
  // setRemoteVideoActive, // isScreenRecording,
  // handleScreenRecording,
}) => {
  const handleEndButtonClick = () => {
    handleCloseMeeting();
  };

  // Participant User
  if (!isAdminOrCM) {
    return (
      <div className="flex items-center justify-center gap-4 w-full">
        {/* Step 1: Connect Button */}
        {!connected && (
          <Button
            icon="pi pi-wifi"
            tooltip="Connect to server"
            tooltipOptions={{ position: "top" }}
            className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
            onClick={handleStartCall}
          />
        )}

        {/* Step 2: Join Meeting Button */}
        {connected && !hasJoined && (
          <Button
            icon="pi pi-sign-in"
            tooltip="Join meeting"
            tooltipOptions={{ position: "top" }}
            className="bg-[#10b981] hover:bg-[#059669] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
            onClick={handleJoinMeeting}
          />
        )}

        {/* Step 3: Start Meeting Button */}
        {hasJoined && !callStarted && (
          <Button
            icon="pi pi-play"
            tooltip="Start meeting"
            tooltipOptions={{ position: "top" }}
            className="bg-[#f59e0b] hover:bg-[#d97706] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
            onClick={handleStartTheCall}
          />
        )}

        {/* Audio Toggle - Only show after call started */}
        {callStarted && (
          <Button
            icon={isAudioOn ? "pi pi-microphone" : "pi pi-volume-off"}
            tooltip={isAudioOn ? "Mute" : "Unmute"}
            tooltipOptions={{ position: "top" }}
            className={`${
              isAudioOn
                ? "bg-[#3f3f46] hover:bg-[#52525b]"
                : "bg-[#dc2626] hover:bg-[#b91c1c]"
            } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
            onClick={toggleAudio}
          />
        )}

        {/* Video Toggle - Only show after call started */}
        {callStarted && (
          <Button
            icon={isVideoOn ? "pi pi-video" : "pi pi-eye-slash"}
            tooltip={isVideoOn ? "Turn off camera" : "Turn on camera"}
            tooltipOptions={{ position: "top" }}
            className={`${
              isVideoOn
                ? "bg-[#3f3f46] hover:bg-[#52525b]"
                : "bg-[#dc2626] hover:bg-[#b91c1c]"
            } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
            onClick={toggleVideo}
          />
        )}

        {/* End Call Button - Only show after call started */}
        {callStarted && (
          <Button
            icon="pi pi-phone"
            tooltip="End meeting"
            tooltipOptions={{ position: "top" }}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl rotate-[135deg]"
            onClick={handleEndButtonClick}
          />
        )}
      </div>
    );
  }

  // Admin / Host User
  return (
    <div className="flex items-center justify-center gap-4 w-full">
      {/* Step 1: Connect Button */}
      {!connected && (
        <Button
          icon="pi pi-wifi"
          tooltip="Connect to server"
          tooltipOptions={{ position: "top" }}
          className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
          onClick={handleStartCall}
        />
      )}

      {/* Step 2: Join Meeting Button */}
      {connected && !hasJoined && (
        <Button
          icon="pi pi-sign-in"
          tooltip="Join meeting"
          tooltipOptions={{ position: "top" }}
          className="bg-[#10b981] hover:bg-[#059669] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
          onClick={handleJoinMeeting}
        />
      )}

      {/* Step 3: Start Meeting Button */}
      {hasJoined && !callStarted && (
        <Button
          icon="pi pi-play"
          tooltip="Start meeting"
          tooltipOptions={{ position: "top" }}
          className="bg-[#f59e0b] hover:bg-[#d97706] text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
          onClick={handleStartTheCall}
        />
      )}

      {/* Audio Toggle - Only show after call started */}
      {callStarted && (
        <Button
          icon={isAudioOn ? "pi pi-microphone" : "pi pi-volume-off"}
          tooltip={isAudioOn ? "Mute" : "Unmute"}
          tooltipOptions={{ position: "top" }}
          className={`${
            isAudioOn
              ? "bg-[#3f3f46] hover:bg-[#52525b]"
              : "bg-[#dc2626] hover:bg-[#b91c1c]"
          } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
          onClick={toggleAudio}
        />
      )}

      {/* Video Toggle - Only show after call started */}
      {callStarted && (
        <Button
          icon={isVideoOn ? "pi pi-video" : "pi pi-eye-slash"}
          tooltip={isVideoOn ? "Turn off camera" : "Turn on camera"}
          tooltipOptions={{ position: "top" }}
          className={`${
            isVideoOn
              ? "bg-[#3f3f46] hover:bg-[#52525b]"
              : "bg-[#dc2626] hover:bg-[#b91c1c]"
          } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
          onClick={toggleVideo}
        />
      )}

      {/* Analyze Button - Only show after call started */}
      {isAdminOrCM && callStarted && (
        <Button
          icon={isAnalyzing ? "pi pi-stop-circle" : "pi pi-eye"}
          tooltip={isAnalyzing ? "Stop analysis" : "Start analysis"}
          tooltipOptions={{ position: "top" }}
          className={`${
            isAnalyzing
              ? "bg-[#ea580c] hover:bg-[#c2410c]"
              : "bg-[#7c3aed] hover:bg-[#6d28d9]"
          } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
          onClick={handleAnalyzeToggle}
        />
      )}

      {/* Screen Recording Button - Only show for Admin after call started */}
      {/* {isAdminOrCM && callStarted && (
        <Button
          icon={isScreenRecording ? "pi pi-stop" : "pi pi-desktop"}
          tooltip={
            isScreenRecording
              ? "Stop screen recording"
              : "Start screen recording"
          }
          tooltipOptions={{ position: "top" }}
          className={`${
            isScreenRecording
              ? "bg-[#dc2626] hover:bg-[#b91c1c]"
              : "bg-[#0891b2] hover:bg-[#0e7490]"
          } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
          onClick={handleScreenRecording}
        />
      )} */}

      {/* End Call Button - Only show after call started */}
      {callStarted && (
        <Button
          icon="pi pi-phone"
          tooltip="End meeting"
          tooltipOptions={{ position: "top" }}
          disabled={isAdminOrCM && !hasAnalysisStarted}
          className={`${
            isAdminOrCM && !hasAnalysisStarted
              ? "bg-red-500 cursor-not-allowed"
              : "bg-[#dc2626] hover:bg-[#b91c1c]"
          } text-white border-none rounded-full w-14 h-14 flex items-center justify-center transition-all shadow-lg hover:shadow-xl rotate-[135deg]`}
          onClick={handleEndButtonClick}
        />
      )}
    </div>
  );
};
